import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { FolderKanban, Users, CircleCheck, AlertTriangle, Plus, Download } from "lucide-react";
import PageHeader from "../common/PageHeader";
import StatsCards, { StatCardConfig } from "../common/StatsCards";
import Button from "../common/Button";
import ProjectCardsGrid from "./ProjectCardsGrid";
import AddProjectModel from "./AddProjectModel";
import EditProjectModel from "./EditProjectModel";
import ViewProjectModal from "./ViewProjectModal";
import { ConfirmationModal } from "../common";
import { ShimmerBlock } from "../common/Skeleton";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import {
  ProjectViewRecord,
  useDeleteProjectMutation,
  useGetProjectsQuery,
  useGetProjectStatsQuery,
  useLazyDownloadProjectReportQuery,
  useLazyGetProjectByIdQuery,
} from "../../store/apis/resource-allocation/project-management.api";
import { useGetResourceStatsQuery } from "../../store/apis/resource-allocation/resource-allocation.api";
import { getProjectHealth } from "../../utils/projectProgress";

const PAGE_SIZE = 8;

const statCards: StatCardConfig[] = [
  {
    key: "activeProjects",
    label: "Active Projects",
    icon: FolderKanban,
    iconBg: "var(--color-primary-50)",
    iconColor: "var(--color-primary-600)",
  },
  {
    key: "peopleAllocated",
    label: "People Allocated",
    icon: Users,
    iconBg: "var(--color-info-light)",
    iconColor: "var(--color-info)",
  },
  {
    key: "onTrackPct",
    label: "On Track",
    icon: CircleCheck,
    iconBg: "var(--color-success-light)",
    iconColor: "var(--color-success-dark)",
    suffix: "%",
  },
  {
    key: "atRisk",
    label: "At Risk",
    icon: AlertTriangle,
    iconBg: "var(--color-error-light)",
    iconColor: "var(--color-error-dark)",
  },
];

const mapUserRowToProjectView = (row: ProjectViewRecord): ProjectViewRecord => ({
  id: row.id,
  name: row.name,
  description: row.description,
  clientName: row.clientName,
  capacity: row.capacity,
  startDate: row.startDate,
  endDate: row.endDate,
  domain: row.domain,
  status: row.status,
  poc: row.poc ?? undefined,
  priority: row.priority,
  billable: row.billable ?? false,
  category: row.category,
  inActiveReason: row.inActiveReason || "",
  allocatedResources: row.allocatedResources,
  createdBy: row.createdBy ?? "",
});

const ProjectManagement = () => {
  const canViewProject = useHasPermission(PERMISSIONS.PROJECT_MANAGEMENT_VIEW);
  const canManageProject = useHasPermission(PERMISSIONS.PROJECT_MANAGEMENT_MANAGE);

  const [openCreateModel, setOpenCreateModel] = useState(false);
  const [openEditModel, setOpenEditModel] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectViewRecord | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const [triggerDownload, { isFetching: isDownloading }] = useLazyDownloadProjectReportQuery();
  const { data: stats, isLoading: isLoadingStats } = useGetProjectStatsQuery();
  const { data: resourceStats } = useGetResourceStatsQuery();
  const [triggerFetchProject, { isFetching: isLoadingDetails }] = useLazyGetProjectByIdQuery();
  const [deleteProject, { isLoading: isDeleting }] = useDeleteProjectMutation();

  const {
    data: projectData,
    isLoading: isLoadingProjects,
    error: projectsError,
  } = useGetProjectsQuery({ page: 1, limit: 1000 });

  useEffect(() => {
    if (projectsError) toast.error("Failed to load projects");
  }, [projectsError]);

  const projects = useMemo(() => projectData?.data ?? [], [projectData]);
  const totalProjects = projectData?.pagination?.total ?? projects.length;

  const healthStats = useMemo(() => {
    let onTrack = 0;
    let atRisk = 0;
    for (const p of projects) {
      const health = getProjectHealth(p.status, p.endDate);
      if (health.label === "On Track") onTrack += 1;
      if (health.label === "At Risk") atRisk += 1;
    }
    const onTrackPct = projects.length > 0 ? Math.round((onTrack / projects.length) * 100) : 0;
    return { onTrackPct, atRisk };
  }, [projects]);

  const statsValues = useMemo(
    () => ({
      activeProjects: stats?.data?.totalProjects ?? totalProjects,
      peopleAllocated: resourceStats?.allocatedResources ?? 0,
      onTrackPct: healthStats.onTrackPct,
      atRisk: healthStats.atRisk,
    }),
    [stats, totalProjects, resourceStats, healthStats],
  );

  const visibleProjects = projects.slice(0, visibleCount);
  const hasMore = visibleCount < projects.length;

  const handleViewDetails = async (projectId: string) => {
    try {
      setOpenViewModal(true);
      const project = await triggerFetchProject(projectId).unwrap();
      setSelectedProject(project);
    } catch (err) {
      toast.error("Failed to fetch project details");
      console.error(err);
    }
  };

  const handleEditClick = async (id: string) => {
    try {
      const project: ProjectViewRecord = await triggerFetchProject(id).unwrap();
      setSelectedProject(project);
      setOpenEditModel(true);
    } catch (err) {
      toast.error("Failed to fetch project details");
    }
  };

  const handleDelete = async () => {
    if (!selectedProject?.id) return;
    try {
      await deleteProject(selectedProject.id).unwrap();
      toast.success("Project deleted successfully");
      setOpenDeleteConfirmation(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to delete project");
    }
  };

  const handleDownloadProject = async () => {
    try {
      const blob = await triggerDownload({}).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.download = `Project_Report_${date}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Project report downloaded successfully");
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.error("Failed to download project report");
    }
  };

  const handleCardView = (p: ProjectViewRecord) => handleViewDetails(p.id);
  const handleCardEdit = (p: ProjectViewRecord) => handleEditClick(p.id);
  const handleCardDelete = (p: ProjectViewRecord) => {
    setSelectedProject(mapUserRowToProjectView(p));
    setOpenDeleteConfirmation(true);
  };

  const cardsLoading = isLoadingProjects || isLoadingStats;

  return (
    <>
      <div className="w-full">
        <PageHeader
          title="Project Management"
          description="Active engagements and delivery health"
          breadcrumbs={[{ label: "Home", path: "/dashboard" }, { label: "Projects" }]}
          className="mb-5 sm:mb-6"
          actions={
            <>
              <Button
                appearance="secondary2"
                onClick={handleDownloadProject}
                loading={isDownloading}
                icon={<Download className="w-4 h-4" />}
              >
                Export
              </Button>
              {canManageProject && (
                <Button
                  appearance="primary"
                  onClick={() => setOpenCreateModel(true)}
                  icon={<Plus className="w-4 h-4" />}
                >
                  New Project
                </Button>
              )}
            </>
          }
        />

        <div className="mb-6">
          <StatsCards
            variant="simple-icon"
            valueOnTop
            stats={statsValues as unknown as Record<string, unknown>}
            cards={statCards}
            loading={cardsLoading}
            containerClassName="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5"
          />
        </div>

        {isLoadingProjects ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="bg-[var(--color-bg-base)] border border-[var(--color-border-secondary)] rounded-[18px] p-4 sm:p-5 shadow-[var(--shadow-sm)] space-y-4"
              >
                <div className="flex items-center gap-[13px]">
                  <ShimmerBlock className="w-[46px] h-[46px] rounded-[13px] flex-none" />
                  <div className="flex-1 space-y-2">
                    <ShimmerBlock className="h-4 w-2/3" />
                    <ShimmerBlock className="h-3 w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <ShimmerBlock className="h-3 w-24" />
                  <ShimmerBlock className="h-2 w-full rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ) : visibleProjects.length === 0 ? (
          <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-secondary)] rounded-[18px] shadow-[var(--shadow-sm)] py-16 flex flex-col items-center justify-center text-center px-6">
            <div className="text-[15px] font-bold text-[var(--color-text-primary)]">
              No projects found
            </div>
            <p className="text-[13px] text-[var(--color-text-tertiary)] mt-1">
              New projects will appear here once they are created.
            </p>
          </div>
        ) : (
          <>
            <ProjectCardsGrid
              projects={visibleProjects}
              canView={canViewProject}
              canManage={canManageProject}
              onView={handleCardView}
              onEdit={handleCardEdit}
              onDelete={handleCardDelete}
            />

            {hasMore && (
              <div className="mt-6 flex flex-col items-center gap-2">
                <Button
                  appearance="secondary2"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                >
                  Load more
                </Button>
                <span className="text-[12px] font-medium text-[var(--color-text-tertiary)]">
                  Showing {visibleProjects.length} of {projects.length} projects
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {openCreateModel && (
        <AddProjectModel
          onClose={() => setOpenCreateModel(false)}
          onSuccess={() => setOpenCreateModel(false)}
        />
      )}
      {openEditModel && (
        <EditProjectModel
          data={selectedProject}
          onClose={() => setOpenEditModel(false)}
          loading={isLoadingDetails}
          onSuccess={() => setOpenEditModel(false)}
        />
      )}
      {openViewModal && selectedProject && (
        <ViewProjectModal
          project={selectedProject}
          loading={isLoadingDetails}
          onClose={() => setOpenViewModal(false)}
        />
      )}

      <ConfirmationModal
        title="Delete Project"
        message="Are you sure you want to delete this project?"
        isOpen={openDeleteConfirmation}
        onClose={() => setOpenDeleteConfirmation(false)}
        onConfirm={handleDelete}
        type="danger"
      />
    </>
  );
};

export default ProjectManagement;